import { useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ReCAPTCHA } from "@/lib/vendor";
import * as yup from "yup";
import apiService from "@/common/services/apiService";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

interface Props {
  show: boolean;
  handleClose: () => void;
}

const ConnectNowModal: React.FC<Props> = ({ show, handleClose }) => {
  const router = useRouter();
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3 || "";
  const reCaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [captchaError, setCaptchaError] = useState<string>("");
  const dispatch = useDispatch();

  const validateCaptchaV3 = (): Promise<string | null> => {
    return new Promise((resolve) => {
      reCaptchaRef.current?.executeAsync()?.then((value) => {
        resolve(value ?? null);
      });
    });
  };

  const handleSubmit = async (formData: any, actions: any) => {
    const isHuman = await validateCaptchaV3();
    if (!isHuman) {
      setCaptchaError("Invalid captcha.");
      return;
    }
    setCaptchaError("");
    dispatch(showFormLoader());

    const payload = {
      ...formData,
      captcha_token_v3: isHuman,
      page_link: window.location.href,
      page_title: document.title,
    };

    const lst: any = await apiService.post("contact-us", { data: payload });

    dispatch(hideFormLoader());
    actions.resetForm();
    reCaptchaRef.current?.reset();

    if (lst?.data?.success) {
      router.push("/thank-you");
      handleClose();
    } else {
      toast.error(lst?.data?.message || "Something went wrong");
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      {/* Modal Box */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-3 top-3 text-gray-500 hover:text-black dark:hover:text-white"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
          Connect Now
        </h2>

        <Formik
          initialValues={{ name: "", email: "", message: "" }}
          validationSchema={yup.object({
            name: yup.string().required("Name is required."),
            email: yup
              .string()
              .email("Invalid Email")
              .required("Email is required."),
            message: yup.string().required("Message is required."),
          })}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Name *</label>
                <Field
                  type="text"
                  name="name"
                  className="w-full border rounded p-2"
                  placeholder="Enter your name"
                />
                <ErrorMessage
                  name="name"
                  component="p"
                  className="text-red-500 text-sm"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium">Email *</label>
                <Field
                  type="email"
                  name="email"
                  className="w-full border rounded p-2"
                  placeholder="Enter your email"
                />
                <ErrorMessage
                  name="email"
                  component="p"
                  className="text-red-500 text-sm"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium">Message *</label>
                <Field
                  as="textarea"
                  name="message"
                  className="w-full border rounded p-2 h-24"
                  placeholder="Write your message"
                />
                <ErrorMessage
                  name="message"
                  component="p"
                  className="text-red-500 text-sm"
                />
              </div>

              <div>
                <ReCAPTCHA
                  style={{ display: "inline" }}
                  theme="light"
                  size="invisible"
                  sitekey={sitekey}
                  ref={reCaptchaRef}
                />
                {captchaError && (
                  <p className="text-red-500 text-sm">{captchaError}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  onClick={handleClose}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ConnectNowModal;
